import { NotificationNext } from '@/constants/notificationNext';
import {
  SHOW_OFFER_PARAM,
  INTERVIEW_ID_PARAM,
  STAGE_PARAM,
  SHOW_DATE_PROPOSE_PARAM,
} from '@/constants/queryParams';
import { ADMIN_ROLE, CLIENT_ROLE, RECRUITER_ROLE } from '@/constants/roles';
import {
  CANDIDATES,
  CANDIDATES_SPECIFICATION_SETTINGS,
  GUEST,
  JOBS,
  MESSAGES,
  RATE,
} from '@/constants/routes';

export type NotificationDeepLinkParams = {
  next: string | null;
  user_id: string | null;
  candidate_id: string | null;
  job_id: string | null;
  interview_id: string | null;
  feedback_id: string | null;
  stage_name: string | null;
};

/** Build in-app path for email notification deep links (`type` query param). */
export function resolveNotificationDeepLink(
  role: string | undefined,
  params: NotificationDeepLinkParams,
): string {
  const {
    next,
    user_id,
    candidate_id,
    job_id,
    interview_id,
    feedback_id,
    stage_name,
  } = params;

  const search = new URLSearchParams();
  const isClient = role === CLIENT_ROLE;
  const isRecruiter = role === RECRUITER_ROLE;
  const isAdmin = role === ADMIN_ROLE;

  if (isClient || isRecruiter) {
    if (next === NotificationNext.ProposeDates) {
      if (interview_id) {
        search.set(INTERVIEW_ID_PARAM, interview_id);
      }
      if (stage_name) {
        search.set(STAGE_PARAM, stage_name);
      }
      search.set(SHOW_DATE_PROPOSE_PARAM, 'true');
      return `${CANDIDATES}?filter=in-process&${search.toString()}`;
    }
    if (next === NotificationNext.OpenTasks) {
      if (interview_id) {
        search.set('tasks', interview_id);
      }
      return `${CANDIDATES}?filter=in-process&${search.toString()}`;
    }
    if (next === NotificationNext.StageOrFeedback) {
      if (stage_name) {
        search.set('stage', stage_name);
      }
      if (isClient) {
        if (interview_id) {
          search.set('tasks', interview_id);
        }
        return `${CANDIDATES}?filter=in-process&${search.toString()}`;
      }
      if (interview_id) {
        search.set('feedback', interview_id);
      }
      return `${CANDIDATES}?filter=in-process&${search.toString()}`;
    }
    if (next === NotificationNext.CandidateHistory) {
      if (candidate_id) {
        search.set('history', candidate_id);
      }
      if (interview_id) {
        search.set('interview_id', interview_id);
      }
      if (stage_name) {
        search.set('stage', stage_name);
      }
      return `${CANDIDATES}?filter=in-process&${search.toString()}`;
    }
    if (next === NotificationNext.FeedbackDetail) {
      if (candidate_id) {
        search.set('history', candidate_id);
      }
      if (interview_id) {
        search.set('interview_id', interview_id);
        search.set('feedback_interview_id', interview_id);
      }
      if (feedback_id) {
        search.set('feedback_id', feedback_id);
      }
      if (stage_name) {
        search.set('stage_name', stage_name);
      }
      return `${CANDIDATES}?filter=in-process&${search.toString()}`;
    }
    if (
      next === NotificationNext.ShowOffer ||
      next === NotificationNext.ShowOfferAlt
    ) {
      if (candidate_id) {
        search.set(SHOW_OFFER_PARAM, candidate_id);
      }
      if (interview_id) {
        search.set(INTERVIEW_ID_PARAM, interview_id);
      }
      return `${CANDIDATES}?filter=in-process&${search.toString()}`;
    }
    if (
      next === NotificationNext.RejectedFeedback ||
      next === NotificationNext.RejectedFeedbackAlt
    ) {
      if (isRecruiter && candidate_id) {
        search.set('history', candidate_id);
      }
      if (interview_id) {
        search.set('interview_id', interview_id);
        search.set('feedback_interview_id', interview_id);
      }
      search.set('rejected_feedback', '1');
      if (stage_name) {
        search.set('stage_name', stage_name);
      }
      return `${CANDIDATES}?filter=in-process&${search.toString()}`;
    }
    if (next === NotificationNext.InProcessCandidates) {
      return `${CANDIDATES}?filter=in-process`;
    }
    if (next === NotificationNext.ClientTasksOrGuestDates) {
      if (isClient && interview_id) {
        search.set('tasks', interview_id);
      }
      return `${CANDIDATES}?filter=in-process&${search.toString()}`;
    }
    if (next === NotificationNext.CandidatesSpecification) {
      return CANDIDATES_SPECIFICATION_SETTINGS;
    }
    if (next === NotificationNext.Messages) {
      if (user_id && interview_id) {
        return `${MESSAGES}/${user_id}/${interview_id}`;
      }
      return `${MESSAGES}/support`;
    }
    if (next === NotificationNext.Job) {
      return job_id ? `${JOBS}/${job_id}` : JOBS;
    }
    if (next === NotificationNext.RateInterview) {
      return interview_id ? `${RATE}/${interview_id}` : CANDIDATES;
    }
    if (next === NotificationNext.InProcessAlt) {
      return `${CANDIDATES}?filter=in-process`;
    }
    return CANDIDATES;
  }

  if (isAdmin) {
    if (next === NotificationNext.AdminJob && job_id) {
      return `${JOBS}/${job_id}`;
    }
    return JOBS;
  }

  if (
    next === NotificationNext.OpenTasks ||
    next === NotificationNext.InProcessAlt
  ) {
    if (interview_id) {
      search.set('tasks', interview_id);
    }
  } else if (
    next === NotificationNext.ClientTasksOrGuestDates ||
    next === NotificationNext.ProposeDates
  ) {
    if (interview_id) {
      search.set(INTERVIEW_ID_PARAM, interview_id);
    }
    if (stage_name) {
      search.set(STAGE_PARAM, stage_name);
    }
    search.set(SHOW_DATE_PROPOSE_PARAM, 'true');
  } else {
    if (interview_id) {
      search.set('feedback', interview_id);
    }
    if (stage_name) {
      search.set('stage', stage_name);
    }
    if (candidate_id) {
      search.set('review', '1');
    }
  }
  const query = search.toString();
  return query ? `${GUEST}?${query}` : GUEST;
}

export type MagicLinkMfaBootstrap = {
  mfaToken: string;
  mfaMethod?: 'email' | 'totp';
  mfaSelectionRequired?: boolean;
  userEmail?: string;
  nextPath: string;
};

export function isMagicLinkMfaChallenge(res: {
  access_token?: string;
  user?: unknown;
  mfa_required?: boolean;
  mfa_selection_required?: boolean;
  mfa_token?: string;
}): boolean {
  return Boolean(
    res.mfa_token &&
    (res.mfa_required || res.mfa_selection_required) &&
    !res.access_token,
  );
}
